import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Printer,
  Download,
  Mail,
  MessageCircle,
  AlertTriangle,
  RotateCw,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import BaseModal from '../../../../components/BaseComponents/BaseModal/BaseModal';
import { useToast } from '../../../../context/ToastContext';
import { useAppSelector } from '../../../../store/hooks';
import { selectUser } from '../../../../store/selectors';
import {
  reviewDispatch,
  executeDispatch,
  fetchCentreFile,
  retryDispatches,
  errorMessage,
  openPendingWindow,
  showBlobIn,
  saveBlob,
} from '../../../../api/document/documentCentreApi';
import { CHANNEL_LABEL, RESULT, formatStamp, hasPermission, canSendDocuments, plural } from '../documentCentre.constants';
import s from './DispatchReviewModal.module.css';

/**
 * The review window (SUNSKY Documents spec v1.2, §11-§13).
 *
 * Shows, before anything happens: the exact documents and versions, the
 * bookings, the actual recipients, how many separate messages, the message
 * text, what is blocked and why, missing travel components and earlier
 * dispatches. Everything the server will skip is listed as skipped; nothing
 * is dropped silently. Execution repeats every check on the server and refuses
 * a message that changed since this review.
 */

const CHANNELS = [
  { key: 'print', label: 'Print', Icon: Printer },
  { key: 'download', label: 'Download PDF', Icon: Download },
  { key: 'email', label: 'Email', Icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
];

const newToken = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const hex = () => Math.floor(Math.random() * 16).toString(16);
  return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, hex);
};

const actionLabel = (channel, review) => {
  const docs = review?.summary?.process || 0;
  const messages = review?.summary?.messages || 0;
  if (channel === 'email') return `Send ${plural(messages, 'email')}`;
  if (channel === 'print') return `Print ${plural(docs, 'document')}`;
  if (channel === 'download') return `Download ${plural(docs, 'document')}`;
  return 'Close';
};

const Previous = ({ list }) => {
  if (!list?.length) return <span className={s.muted}>None</span>;
  return list.map((p) => (
    <span key={p.id} className={s.muted}>
      {formatStamp(p.at)}, {CHANNEL_LABEL[p.channel]}, {RESULT[p.status]?.label || p.status} (v{p.version})
    </span>
  ));
};

const DispatchReviewModal = ({ open, documentIds, initialChannel = 'email', onClose, onFinished, title }) => {
  const { showToast } = useToast();
  const user = useAppSelector(selectUser);
  const canSend = canSendDocuments(user);
  const canAlternative = hasPermission(user, 'document.dispatch.alternative_recipient');

  const [channel, setChannel] = useState(initialChannel);
  const [purpose, setPurpose] = useState('customer');
  const [completeSets, setCompleteSets] = useState(false);
  const [bundle, setBundle] = useState(false);
  const [useAlt, setUseAlt] = useState(false);
  const [alt, setAlt] = useState({ address: '', name: '' });
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState({ note: '', address: '', name: '' });
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('review');
  const [results, setResults] = useState(null);
  const [waState, setWaState] = useState({});
  const [openText, setOpenText] = useState({});
  const requestId = useRef(0);
  const idsKey = (documentIds || []).join(',');

  // Typing in the note or the alternative address re-checks after a pause,
  // so the preview always shows what will really go out.
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setTyped((prev) =>
          prev.note === note && prev.address === alt.address && prev.name === alt.name
            ? prev
            : { note, address: alt.address, name: alt.name }
        ),
      450
    );
    return () => clearTimeout(timer);
  }, [note, alt]);

  const body = useMemo(
    () => ({
      documentIds: idsKey ? idsKey.split(',').map(Number) : [],
      channel,
      purpose,
      completeTravelSets: completeSets,
      bundleSameRecipient: channel === 'email' && bundle,
      note: typed.note,
      recipientOverride:
        useAlt && typed.address.trim() ? { address: typed.address.trim(), name: typed.name.trim() } : null,
    }),
    [idsKey, channel, purpose, completeSets, bundle, typed, useAlt]
  );

  useEffect(() => {
    if (!open || phase !== 'review' || !body.documentIds.length) return;
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    reviewDispatch(body)
      .then((data) => {
        if (id === requestId.current) setReview(data);
      })
      .catch(async (err) => {
        if (id !== requestId.current) return;
        const message = await errorMessage(err, 'Could not prepare the review');
        setError(message);
        setReview(null);
        showToast(message, 'error');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [open, body, phase, showToast]);

  const switchChannel = (key) => {
    if (key === channel || phase !== 'review') return;
    setChannel(key);
    setReview(null);
    if (key !== 'email') setBundle(false);
    if (key !== 'email' && key !== 'whatsapp') setUseAlt(false);
  };

  const close = () => {
    if (phase === 'executing') return;
    onClose?.();
  };

  const runEmail = async () => {
    setPhase('executing');
    try {
      const data = await executeDispatch({
        ...body,
        reviewToken: review.reviewToken,
        messages: review.messages.map((m) => ({ key: m.key, fingerprint: m.fingerprint })),
      });
      setResults(data);
      setPhase('results');
      const { sent, failed, skipped } = data.summary;
      if (failed) showToast(`${sent} sent, ${failed} failed. Retry the failed ones from the results.`, 'error');
      else if (!sent) showToast('Nothing was sent. See the reasons in the results.', 'error');
      else showToast(`${plural(sent, 'email')} sent${skipped ? `, ${skipped} skipped` : ''}`, 'success');
      onFinished?.();
    } catch (err) {
      setPhase('review');
      showToast(await errorMessage(err, 'Sending failed'), 'error');
    }
  };

  const runFile = async () => {
    // Opened inside the click so the popup blocker lets it through.
    const win = channel === 'print' ? openPendingWindow() : null;
    setPhase('executing');
    try {
      const data = await executeDispatch({
        ...body,
        reviewToken: review.reviewToken,
        reviewedProcessIds: review.documents.filter((d) => d.action === 'process').map((d) => d.id),
      });
      if (!data.fileToken) {
        win?.close();
        setResults(data);
        setPhase('results');
        showToast('Nothing could be processed. See the reasons in the results.', 'error');
        return;
      }
      const blob = await fetchCentreFile(data.fileToken);
      if (channel === 'print') showBlobIn(win, blob);
      else saveBlob(blob, data.filename || 'documents.pdf');
      setResults(data);
      setPhase('results');
      showToast(channel === 'print' ? 'Print file opened' : 'Download ready', 'success');
      onFinished?.();
    } catch (err) {
      win?.close();
      setPhase('review');
      showToast(await errorMessage(err, channel === 'print' ? 'Printing failed' : 'Download failed'), 'error');
    }
  };

  /**
   * WhatsApp has no integration: each message is one manual share. Recorded
   * as "share action started" (spec §17), then the documents are downloaded so
   * they can be attached in the chat.
   */
  const openWhatsApp = async (message) => {
    const win = window.open('', '_blank');
    setWaState((prev) => ({ ...prev, [message.key]: { status: 'working' } }));
    try {
      const data = await executeDispatch({
        ...body,
        reviewToken: review.reviewToken,
        messages: [{ key: message.key, fingerprint: message.fingerprint }],
      });
      const result = data.results?.[0] || {};
      if (result.waUrl) {
        if (win && !win.closed) win.location.href = result.waUrl;
        else window.open(result.waUrl, '_blank');
      } else {
        win?.close();
      }
      setWaState((prev) => ({ ...prev, [message.key]: { status: result.status, error: result.error } }));
      if (result.status !== 'share_started' && result.status !== 'duplicate') {
        showToast(result.error || 'WhatsApp could not be opened', 'error');
        return;
      }
      showToast('WhatsApp opened. Attach the downloaded documents in the chat.', 'success');
      const ids = message.documents.map((d) => d.id);
      const file = await executeDispatch({
        documentIds: ids,
        channel: 'download',
        purpose: 'customer',
        reviewToken: newToken(),
        reviewedProcessIds: ids,
      });
      if (file.fileToken) saveBlob(await fetchCentreFile(file.fileToken), file.filename || 'documents.zip');
      onFinished?.();
    } catch (err) {
      win?.close();
      setWaState((prev) => ({ ...prev, [message.key]: { status: 'failed' } }));
      showToast(await errorMessage(err, 'WhatsApp could not be opened'), 'error');
    }
  };

  const retryFailed = async () => {
    const ids = (results?.results || []).filter((r) => r.status === 'failed').flatMap((r) => r.dispatchIds || []);
    if (!ids.length) return;
    setPhase('executing');
    try {
      const data = await retryDispatches(ids);
      const byId = new Map((data.results || []).map((r) => [r.dispatchId, r]));
      setResults((prev) => ({
        ...prev,
        results: prev.results.map((r) => {
          if (r.status !== 'failed') return r;
          const outcomes = (r.dispatchIds || []).map((id) => byId.get(id)).filter(Boolean);
          if (!outcomes.length) return r;
          const allSent = outcomes.every((o) => o.status === 'sent');
          return { ...r, status: allSent ? 'sent' : outcomes[0].status, error: allSent ? null : outcomes[0].error };
        }),
      }));
      const { sent, failed } = data.summary;
      showToast(
        failed ? `Retry: ${sent} sent, ${failed} failed again` : `Retry: ${plural(sent, 'item')} sent`,
        failed ? 'error' : 'success'
      );
      onFinished?.();
    } catch (err) {
      showToast(await errorMessage(err, 'Retry failed'), 'error');
    } finally {
      setPhase('results');
    }
  };

  const messageByKey = useMemo(() => new Map((review?.messages || []).map((m) => [m.key, m])), [review]);

  const footer = (
    <div className={s.footer}>
      <span className={s.footerNote}>
        {phase === 'review' && review
          ? `${review.summary.process} to process, ${review.summary.skip} skipped`
          : phase === 'executing'
            ? 'Processing...'
            : ''}
      </span>
      <div className={s.footerButtons}>
        {phase === 'results' && (results?.results || []).some((r) => r.status === 'failed') && (
          <button type="button" className={s.secondaryBtn} onClick={retryFailed}>
            <RotateCw size={14} /> Retry failed only
          </button>
        )}
        <button type="button" className={s.secondaryBtn} onClick={close} disabled={phase === 'executing'}>
          {phase === 'results' || channel === 'whatsapp' ? 'Close' : 'Cancel'}
        </button>
        {phase !== 'results' && channel !== 'whatsapp' && (
          <button
            type="button"
            className={s.primaryBtn}
            disabled={phase === 'executing' || loading || !review || !review.summary.process || ((channel === 'email') && !canSend)}
            onClick={channel === 'email' ? runEmail : runFile}
          >
            {phase === 'executing' ? 'Processing...' : actionLabel(channel, review)}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <BaseModal open={open} onClose={close} title={title || 'Review before processing'} width="1020px" footer={footer}>
      <div className={s.tabs} role="tablist" aria-label="Processing channel">
        {CHANNELS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={channel === key}
            className={`${s.tab} ${channel === key ? s.tabActive : ''}`}
            onClick={() => switchChannel(key)}
            disabled={phase !== 'review' || ((key === 'email' || key === 'whatsapp') && !canSend)}
            title={(key === 'email' || key === 'whatsapp') && !canSend ? 'No permission to send documents' : undefined}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {phase === 'results' ? (
        <Results channel={channel} results={results} messageByKey={messageByKey} />
      ) : loading && !review ? (
        <div className={s.loading}>
          <span className={s.spinner} /> Checking documents, recipients and release...
        </div>
      ) : error ? (
        <div className={s.error}>{error}</div>
      ) : review ? (
        <>
          <div className={s.summary}>
            <span className={`${s.summaryItem} ${s.summaryProcess}`}>
              <strong>{review.summary.process}</strong>to process
            </span>
            <span className={`${s.summaryItem} ${s.summarySkip}`}>
              <strong>{review.summary.skip}</strong>skipped
            </span>
            <span className={s.summaryItem}>
              <strong>{review.summary.bookings}</strong>
              {review.summary.bookings === 1 ? 'booking' : 'bookings'}
            </span>
            {(channel === 'email' || channel === 'whatsapp') && (
              <span className={s.summaryItem}>
                <strong>{review.summary.messages}</strong>separate {review.summary.messages === 1 ? 'message' : 'messages'}
              </span>
            )}
            {review.summary.added > 0 && (
              <span className={s.summaryItem}>
                <strong>{review.summary.added}</strong>added to complete travel sets
              </span>
            )}
            {loading && <span className={s.summaryItem}>Updating...</span>}
          </div>

          <div className={s.options}>
            <span className={s.optionsTitle}>Options</span>
            {(channel === 'print' || channel === 'download') && (
              <div className={s.optionRow}>
                <label className={s.check}>
                  <input type="radio" checked={purpose === 'customer'} onChange={() => setPurpose('customer')} />
                  Customer-ready (released documents only)
                </label>
                <label className={s.check}>
                  <input type="radio" checked={purpose === 'internal'} onChange={() => setPurpose('internal')} />
                  Internal review copy (documents that are not released are marked)
                </label>
              </div>
            )}
            <div className={s.optionRow}>
              <label className={s.check}>
                <input type="checkbox" checked={completeSets} onChange={(e) => setCompleteSets(e.target.checked)} />
                Add each booking's other released travel documents
              </label>
              {channel === 'email' && (
                <label className={s.check}>
                  <input type="checkbox" checked={bundle} onChange={(e) => setBundle(e.target.checked)} />
                  One email per customer and recipient, across bookings
                </label>
              )}
            </div>
            {(channel === 'email' || channel === 'whatsapp') && (
              <>
                {canAlternative && (
                  <div className={s.optionRow}>
                    <label className={s.check}>
                      <input type="checkbox" checked={useAlt} onChange={(e) => setUseAlt(e.target.checked)} />
                      Send to an alternative recipient
                    </label>
                    {useAlt && (
                      <>
                        <input
                          className={s.input}
                          placeholder={channel === 'email' ? 'Email address' : 'Mobile number, e.g. +32475123456'}
                          value={alt.address}
                          onChange={(e) => setAlt((a) => ({ ...a, address: e.target.value }))}
                        />
                        <input
                          className={s.input}
                          placeholder="Name (optional)"
                          value={alt.name}
                          onChange={(e) => setAlt((a) => ({ ...a, name: e.target.value }))}
                        />
                      </>
                    )}
                  </div>
                )}
                <textarea
                  className={s.textarea}
                  placeholder="Optional personal note, added to the approved message text"
                  value={note}
                  maxLength={2000}
                  onChange={(e) => setNote(e.target.value)}
                />
              </>
            )}
          </div>

          {review.warnings?.length > 0 && (
            <div className={s.warnings}>
              {review.warnings.map((w) => (
                <div key={w}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {w}
                </div>
              ))}
            </div>
          )}

          {(channel === 'email' || channel === 'whatsapp') && review.messages.length > 0 && (
            <>
              <div className={s.sectionTitle}>
                {review.messages.length === 1 ? 'Message' : `${review.messages.length} separate messages`}
              </div>
              {review.messages.map((message) => {
                const wa = waState[message.key];
                return (
                  <div key={message.key} className={s.message}>
                    <div className={s.messageHead}>
                      <div>
                        <div className={s.messageTo}>
                          To {message.recipient?.name ? `${message.recipient.name}, ` : ''}
                          {message.recipient?.address}
                        </div>
                        <div className={s.messageMeta}>
                          {message.recipient?.roleLabel}. Booking {message.bookingReferences.join(', ')}.{' '}
                          {message.templateName}, {String(message.language || 'en').toUpperCase()}.
                        </div>
                        {message.languageNote && <div className={s.messageMeta}>{message.languageNote}</div>}
                        {message.recipient && !message.recipient.isIntended && (
                          <div className={s.notIntended}>
                            Not the booking contact ({message.recipient.intendedAddress || 'none recorded'}). This does not
                            complete the dispatch to the booking contact.
                          </div>
                        )}
                        {message.subject && <div className={s.messageMeta}>Subject: {message.subject}</div>}
                      </div>
                      {channel === 'whatsapp' && (
                        <button
                          type="button"
                          className={s.waBtn}
                          onClick={() => openWhatsApp(message)}
                          disabled={wa?.status === 'working' || wa?.status === 'share_started' || !canSend}
                        >
                          <MessageCircle size={14} />
                          {wa?.status === 'share_started'
                            ? 'Share started'
                            : wa?.status === 'working'
                              ? 'Opening...'
                              : 'Open WhatsApp'}
                        </button>
                      )}
                    </div>
                    <div className={s.docList}>
                      {message.documents.map((d) => (
                        <span key={d.id} className={s.docPill}>
                          {d.label} {d.number || ''} v{d.version}
                        </span>
                      ))}
                    </div>
                    {message.missing?.length > 0 && (
                      <div className={s.missing}>
                        Partial set. Still missing for this booking:
                        <ul>
                          {message.missing.map((m) => (
                            <li key={`${m.bookingReference}-${m.productId}`}>
                              {m.label}: {m.reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {wa?.error && <div className={s.missing}>{wa.error}</div>}
                    <button
                      type="button"
                      className={s.linkBtn}
                      onClick={() => setOpenText((o) => ({ ...o, [message.key]: !o[message.key] }))}
                    >
                      {openText[message.key] ? 'Hide message text' : 'Show message text'}
                    </button>
                    {openText[message.key] && <pre className={s.preview}>{message.text}</pre>}
                  </div>
                );
              })}
            </>
          )}

          {channel === 'whatsapp' && review.messages.length > 0 && (
            <p className={s.hint}>
              WhatsApp is not integrated. Each message opens a chat with the text filled in and downloads its documents
              for you to attach. It is recorded as a share action started, never as delivered.
            </p>
          )}

          <div className={s.sectionTitle}>Documents</div>
          <div className={s.tableWrap}>
            <table className={s.docTable}>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Booking</th>
                  <th>Outcome</th>
                  <th>Earlier dispatches</th>
                </tr>
              </thead>
              <tbody>
                {review.documents.map((d) => (
                  <tr key={d.id}>
                    <td>
                      {d.typeLabel} {d.documentNumber || ''} <span className={s.muted}>Version {d.version}</span>
                      {d.added && <span className={s.added}>added</span>}
                    </td>
                    <td>
                      {d.bookingReference}
                      <span className={s.muted}>{d.customerName}</span>
                    </td>
                    <td>
                      {d.action === 'process' ? (
                        <span className={s.process}>{d.internalMark ? 'Process as review copy' : 'Process'}</span>
                      ) : (
                        <>
                          <span className={s.skip}>Skip</span>
                          <span className={s.reason}>{d.skipReason}</span>
                        </>
                      )}
                      {d.action === 'process' && d.release?.released && (
                        <span className={s.muted}>{d.release.label}</span>
                      )}
                      {(d.warnings || []).map((w) => (
                        <span key={w.code} className={s.muted} style={{ color: '#b45309' }}>
                          {w.label}
                        </span>
                      ))}
                    </td>
                    <td>
                      <Previous list={d.previousDispatches} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {review.notFound?.length > 0 && (
            <p className={s.hint}>{plural(review.notFound.length, 'selected document')} no longer exist and cannot be processed.</p>
          )}
        </>
      ) : null}
    </BaseModal>
  );
};

const Results = ({ channel, results, messageByKey }) => {
  if (!results) return null;
  const skipped = results.skipped || [];
  return (
    <div>
      {(channel === 'email' || channel === 'whatsapp') &&
        (results.results || []).map((r) => {
          const message = messageByKey.get(r.key);
          const meta = RESULT[r.status] || { label: r.status, color: '#475569' };
          return (
            <div key={r.key} className={s.result}>
              <div>
                <div className={s.messageTo}>To {message?.recipient?.address || r.key.split('|').pop()}</div>
                <div className={s.messageMeta}>
                  {(message?.documents || []).map((d) => `${d.label} ${d.number || ''}`).join(', ')}
                </div>
                {r.error && <div className={s.reason}>{r.error}</div>}
              </div>
              <span className={s.resultStatus} style={{ color: meta.color }}>
                {r.status === 'sent' ? <CheckCircle2 size={14} /> : r.status === 'failed' ? <XCircle size={14} /> : null}{' '}
                {meta.label}
              </span>
            </div>
          );
        })}
      {(channel === 'print' || channel === 'download') && (
        <div className={s.result}>
          <span>{plural((results.processed || []).length, 'document')} processed</span>
          <span className={s.resultStatus} style={{ color: results.fileToken ? '#059669' : '#dc2626' }}>
            {results.fileToken ? (channel === 'print' ? 'Print file opened' : 'Downloaded') : 'Nothing processed'}
          </span>
        </div>
      )}
      {skipped.length > 0 && (
        <>
          <div className={s.sectionTitle} style={{ marginTop: 14 }}>
            Skipped
          </div>
          {skipped.map((d) => (
            <div key={d.id} className={s.result}>
              <span>{d.documentNumber || `Document ${d.id}`}</span>
              <span className={s.reason}>{d.reason}</span>
            </div>
          ))}
        </>
      )}
      <p className={s.hint} style={{ marginTop: 12 }}>
        Every result is in the dispatch history. Prints and downloads are recorded there too, but never count as a dispatch.
      </p>
    </div>
  );
};

export default DispatchReviewModal;
